/**
 * DEPRECATED: This file is deprecated and will be removed in the future.
 * Use the new API client from @/generated/korza-api-client instead.
 */

import * as dtos from './dtos';
import { tenantCreationRequestSchema } from './tenant-creation-request.schema';

export const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL;

export interface SendNotificationDto {
  title: string;
  body: string;
  websitePath: string;
}

export interface RegisterTestDeviceDto {
  expoPushToken: string;
}

/**
 * Triggers a logout
 */
export class UnauthorizedError extends Error {
  constructor(public readonly response: Response) {
    super('Unauthorized');
  }
}

/** Sign-up was requested for an email that already has an account. */
export class AccountAlreadyExistsError extends Error {
  constructor() {
    super('Account already exists');
  }
}

// Auth functions
async function postAuth<TResponse>(
  path: '/auth/signup' | '/auth/login',
  body: Record<string, string>
): Promise<{ response: Response; data: TResponse | null }> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    credentials: 'include',
  });

  if (!response.ok) {
    return { response, data: null };
  }

  return { response, data: (await response.json()) as TResponse };
}

async function readErrorMessage(
  response: Response,
  fallback: string
): Promise<string> {
  const error = await response.json().catch(() => null);
  return error?.message || fallback;
}

/**
 * Creates a new account and emails it a magic link; the link is never returned
 * here, so the user has to open it from their inbox.
 * Throws {@link AccountAlreadyExistsError} if the email already has an account.
 */
export async function signUp(input: {
  email: string;
  name: string;
  phone: string;
  storeName: string;
}): Promise<{ message: string }> {
  const { response, data } = await postAuth<{ message: string }>(
    '/auth/signup',
    input
  );

  if (data) {
    return data;
  }
  if (response.status === 409) {
    throw new AccountAlreadyExistsError();
  }
  throw new Error(await readErrorMessage(response, 'Failed to create account'));
}

/**
 * Asks for a login magic link. The backend answers the same way whether or not
 * the email has an account, so a successful call does not mean an email was sent.
 */
export async function logIn(email: string): Promise<{ message: string }> {
  const { response, data } = await postAuth<{ message: string }>(
    '/auth/login',
    { email }
  );

  if (data) {
    return data;
  }
  throw new Error(
    await readErrorMessage(response, 'Failed to send magic link')
  );
}

export async function logout() {
  const response = await fetch(`${API_BASE_URL}/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to logout');
  }
}

export async function registerTestDevice(
  tenantSlug: string,
  expoPushToken: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/campaigns/register-test-device`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ expoPushToken }),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to register test device');
  }
}

export async function sendTestNotification(
  tenantSlug: string,
  data: SendNotificationDto
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/test-notifications/send`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to send test notification');
  }
}

export async function checkIsAuthenticated(): Promise<boolean> {
  const response = await fetch(`${API_BASE_URL}/users/self`, {
    method: 'GET',
    credentials: 'include',
  });

  if (response.status === 200) {
    return true;
  } else if (response.status === 401) {
    return false;
  } else {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to check auth status');
  }
}

export async function getTenantCreationRequest(
  slug: string
): Promise<dtos.TenantCreationRequest | null> {
  const response = await fetch(
    `${API_BASE_URL}/tenant-creation-requests/${slug}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to get tenant creation request');
  }

  return tenantCreationRequestSchema.parse(await response.json());
}

export async function cancelShopifySubscription(tenantSlug: string) {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/subscriptions/shopify/cancel`,
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to cancel shopify subscription');
  }

  return true;
}

export async function createShopifyIntegrationForTcr(payload: {
  slug: string;
  code: string;
  hmac: string;
  host: string;
  shop: string;
}) {
  const response = await fetch(
    `${API_BASE_URL}/tenant-creation-requests/${payload.slug}/shopify-integration`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to create shopify integration');
  }

  return response.json();
}

export async function createTcrFromShopifyAppInstall(payload: {
  code: string;
  hmac: string;
  host: string;
  shop: string;
}) {
  const response = await fetch(
    `${API_BASE_URL}/tenant-creation-requests/from-shopify-app`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(
      error.message || 'Failed to create tenant creation request'
    );
  }

  return (await response.json()) as dtos.TenantCreationRequest & {
    magicLink?: string;
  };
}

export async function getTenantCreationRequests() {
  const response = await fetch(
    `${API_BASE_URL}/self/tenant-creation-requests`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to get tenant creation requests');
  }

  return response.json();
}

export async function createNumvemshopIntegration(
  tenantSlug: string,
  nuvemshopAuthorizationCode: string
) {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/integrations/nuvemshop/code`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        nuvemshopAuthorizationCode,
      }),
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to get tenant creation requests');
  }
}

export async function createShopifyIntegration(
  tenantSlug: string,
  shopifyAuthorizationCode: string
) {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/integrations/shopify/code`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        shopifyAuthorizationCode,
      }),
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to get tenant creation requests');
  }
}

export async function getCouponBannerConfig(
  tenantSlug: string
): Promise<dtos.CouponBannerDto | null> {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/app-customization/coupon-banner`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to get coupon banner config');
  }

  return response.json();
}

export async function setCouponBannerConfig(
  tenantSlug: string,
  config: dtos.CouponBannerDto
): Promise<dtos.CouponBannerDto> {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/app-customization/coupon-banner`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || 'Failed to set coupon banner config');
  }

  return response.json();
}

export function getWelcomeNotificationConfig(
  tenantSlug: string
): Promise<dtos.WelcomeNotificationDto | null> {
  return fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/app-customization/welcome-notification`,
    {
      method: 'GET',
      credentials: 'include',
    }
  ).then(async (response) => {
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const error = await response
        .json()
        .catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || 'Failed to get welcome notification');
    }

    const text = await response.text();
    return text === '' ? null : JSON.parse(text);
  });
}

export async function setWelcomeNotificationConfig(
  tenantSlug: string,
  config: dtos.WelcomeNotificationDto
): Promise<dtos.WelcomeNotificationDto> {
  const response = await fetch(
    `${API_BASE_URL}/tenants/${tenantSlug}/app-customization/welcome-notification`,
    {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    }
  );

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: 'Unknown error' }));
    throw new Error(
      error.message || 'Failed to set welcome notification config'
    );
  }

  return response.json();
}
